import { Box, Tooltip } from "@mui/material";
import React from "react";
import { eurToKmf, formatKmf } from "./utils/currency"; // adapter le chemin si besoin

/* ═══════════════════════════════════════════════════════════
   UNITÉS DE VENTE

   Sur un plan weekly ou monthly, le montant saisi est le prix de la période
   entière, pas d'une nuit. L'unité est rappelée sur le nom du tarif, une
   seule fois, pour ne pas alourdir chaque cellule.

   "long_stay" est l'ancienne valeur, lue comme mensuelle.
   ═══════════════════════════════════════════════════════════ */
const UNIT = {
  short_stay: { label: "nuit",    nights: 1  },
  weekly:     { label: "semaine", nights: 7  },
  monthly:    { label: "mois",    nights: 30 },
};

export const normalizeStayType = (t) =>
  t === "long_stay" ? "monthly" : (["short_stay", "weekly", "monthly"].includes(t) ? t : "short_stay");

export const unitOf = (stayType) => UNIT[normalizeStayType(stayType)];
export const isPeriodPlan = (stayType) => normalizeStayType(stayType) !== "short_stay";

/* ═══════════════════════════════════════════════════════════
   GABARIT PARTAGÉ

   Toutes les lignes, en-tête des jours compris, vivent dans UNE SEULE grille
   CSS déclarée par le parent. C'est ce qui garantit l'alignement : deux
   grilles séparées ne peuvent pas avoir des colonnes identiques dès qu'un
   contenu déborde ou qu'une bordure décale d'un pixel.
   ═══════════════════════════════════════════════════════════ */
export const LABEL_COL = 190;
export const ROW_H = 52;

export const COLORS = {
  border:     "#E9EDF2",
  borderSoft: "#F1F5F9",
  label:      "#FBFCFD",
  weekend:    "#FAFBFC",
  selected:   "#4F46E5",
  missing:    "#FEF9E7",
  missingTxt: "#A16207",
  closed:     "#FDF2F2",
  muted:      "#94A3B8",
  text:       "#1E293B",
};

// Base commune à toutes les cellules de jour
const dayCell = {
  height: ROW_H,
  minWidth: 0,
  overflow: "hidden",
  borderLeft: `1px solid ${COLORS.borderSoft}`,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 13,
  transition: "background-color .12s ease",
};

// Colonne de gauche
const labelCell = {
  height: ROW_H,
  backgroundColor: COLORS.label,
  borderRight: `1px solid ${COLORS.border}`,
  px: 2,
  display: "flex",
  alignItems: "center",
  gap: 1,
  fontSize: 13,
  fontWeight: 600,
  color: COLORS.text,
  minWidth: 0,
};

/* Arrondis appliqués aux extrémités d'une sélection continue. */
const selectionRadius = (isFirst, isLast) => ({
  borderTopLeftRadius: isFirst ? 8 : 0,
  borderBottomLeftRadius: isFirst ? 8 : 0,
  borderTopRightRadius: isLast ? 8 : 0,
  borderBottomRightRadius: isLast ? 8 : 0,
});

/**
 * Rend les lignes d'une chambre DANS la grille du parent.
 * Ne déclare aucune grille : renvoie une suite de cellules.
 */
const InventoryGrid = ({
  room,
  visibleDays,
  startIndex,
  selectedDays,
  setSelectedDays,
  editType,
  setEditType,
  editModeRoom,
  setEditModeRoom,
  isSelecting,
  setIsSelecting,
  selectionStart,
  setSelectionStart,
  editRatePlan,
  setEditRatePlan,
  openBulk,
  goToRatePlans,
  allRatePlans = [],
  isLastRoom = false,
}) => {

  const roomIdStr = (room.roomId ?? room._id)?.toString();

  // Les tarifs affichés viennent de roomIds : une ligne apparaît même sans
  // prix saisi, ce qui la rend cliquable pour la remplir.
  const ratePlans = allRatePlans
    .filter(plan => (plan.roomIds || []).map(id => id?.toString?.() ?? id).includes(roomIdStr))
    .map(plan => ({
      id: plan._id.toString(),
      name: plan.name,
      stayType: normalizeStayType(plan.stayType),
    }));

  const hasPrice = room.days?.some(day => day.rates?.some(r => r.price > 0));

  // Démarre une sélection au clic
  const beginSelection = (type, index, planId = null) => {
    setIsSelecting(true);
    setSelectionStart(index);
    setEditModeRoom(room.roomId);
    setEditType(type);
    setEditRatePlan(planId);
    setSelectedDays({ tarif: [], inventory: [], status: [], [type]: [index] });
  };

  // Étend la sélection au survol
  const extendSelection = (type, index, planId = null) => {
    if (!isSelecting || editType !== type || editModeRoom !== room.roomId) return;
    if (type === "tarif" && editRatePlan !== planId) return;
    if (selectionStart === null) return;
    const start = Math.min(selectionStart, index);
    const end = Math.max(selectionStart, index);
    setSelectedDays({
      tarif: [], inventory: [], status: [],
      [type]: Array.from({ length: end - start + 1 }, (_, i) => start + i),
    });
  };

  const endSelection = () => { setIsSelecting(false); setSelectionStart(null); };

  const isDayWeekend = (dateStr) => {
    const d = new Date(`${dateStr}T12:00:00`).getDay();
    return d === 0 || d === 6;
  };

  return (
    <>
      {/* ── Bandeau de la chambre, sur toute la largeur ── */}
      <Box sx={{
        gridColumn: "1 / -1",
        px: 2.5,
        py: 1.75,
        backgroundColor: "#FFFFFF",
        borderTop: `1px solid ${COLORS.border}`,
        display: "flex",
        alignItems: "center",
        gap: 1.5,
      }}>
        <Box sx={{ fontSize: 14, fontWeight: 700, color: COLORS.text }}>
          {room.title}
        </Box>
        {!hasPrice && ratePlans.length > 0 && (
          <Box
            onClick={() => openBulk({ type: "price", roomId: room.roomId })}
            sx={{
              px: 1.25, py: 0.25,
              backgroundColor: COLORS.missing,
              color: COLORS.missingTxt,
              borderRadius: 1.5,
              fontSize: 11,
              fontWeight: 600,
              cursor: "pointer",
              "&:hover": { filter: "brightness(0.97)" },
            }}
          >
            Aucun prix configuré
          </Box>
        )}
      </Box>

      {/* ── Lignes de tarif ── */}
      {ratePlans.length === 0 ? (
        <>
          {/* Une chambre sans aucun tarif n'est pas réservable du tout : plus
              grave qu'un prix manquant, et rien dans la grille ne permet de
              le corriger. L'invitation renvoie donc vers la page Tarifs,
              seule à pouvoir créer un plan. */}
          <Box sx={{
            ...labelCell,
            borderTop: `1px solid ${COLORS.borderSoft}`,
            color: COLORS.missingTxt,
            fontWeight: 500,
          }}>
            Aucun tarif
          </Box>

          <Box
            onClick={() => goToRatePlans?.()}
            sx={{
              gridColumn: "2 / -1",
              height: ROW_H,
              borderTop: `1px solid ${COLORS.borderSoft}`,
              borderLeft: `1px solid ${COLORS.borderSoft}`,
              backgroundColor: COLORS.missing,
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 2,
              cursor: goToRatePlans ? "pointer" : "default",
              "&:hover": { filter: goToRatePlans ? "brightness(0.98)" : "none" },
            }}
          >
            <Box sx={{ fontSize: 13, color: COLORS.missingTxt }}>
              Cette chambre n'a aucun tarif, elle ne peut pas être réservée.
            </Box>
            {goToRatePlans && (
              <Box sx={{
                fontSize: 13,
                fontWeight: 700,
                color: "var(--k-green)",
                textDecoration: "underline",
                whiteSpace: "nowrap",
              }}>
                Créer un tarif
              </Box>
            )}
          </Box>
        </>
      ) : (
        ratePlans.map(plan => {
          const unit = unitOf(plan.stayType);
          const period = isPeriodPlan(plan.stayType);

          return (
            <React.Fragment key={plan.id}>
              <Tooltip
                title={period
                  ? `${plan.name} — prix pour ${unit.label === "mois" ? "un mois" : "une semaine"} entier${unit.label === "mois" ? "" : "e"}`
                  : plan.name}
                placement="right"
              >
                <Box
                  onClick={() => openBulk({ type: "price", roomId: room.roomId, ratePlanId: plan.id })}
                  sx={{
                    ...labelCell,
                    borderTop: `1px solid ${COLORS.borderSoft}`,
                    cursor: "pointer",
                    "&:hover": { backgroundColor: "#F3F6F9" },
                  }}
                >
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Box sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {plan.name}
                    </Box>
                    {/* L'unité de vente n'apparaît qu'ici, jamais dans les cellules */}
                    {period && (
                      <Box sx={{ fontSize: 10, fontWeight: 700, color: "var(--k-green)", lineHeight: 1.2 }}>
                        prix / {unit.label}
                      </Box>
                    )}
                  </Box>
                  <Box sx={{ fontSize: 11, color: "#CBD5E1", flexShrink: 0 }}>✎</Box>
                </Box>
              </Tooltip>

              {visibleDays.map((dateStr, index) => {
                const dayData = room.days[startIndex + index];
                const rate = dayData?.rates?.find(r =>
                  (r.ratePlanId?._id || r.ratePlanId)?.toString() === plan.id
                );
                const price = rate?.price ?? null;
                const priceKMF = price === null ? null : (rate?.priceKMF ?? eurToKmf(price));

                const isSelected =
                  editType === "tarif" &&
                  editModeRoom === room.roomId &&
                  editRatePlan === plan.id &&
                  selectedDays.tarif?.includes(index);

                const isFirst = isSelected && !selectedDays.tarif?.includes(index - 1);
                const isLast  = isSelected && !selectedDays.tarif?.includes(index + 1);

                const title = price === null
                  ? (period
                      ? `Définir le prix d'${unit.label === "mois" ? "un mois" : "une semaine"}`
                      : "Définir un prix")
                  : (period
                      ? `${price} € / ${unit.label} · ${formatKmf(priceKMF)} KMF · soit ≈ ${Math.round(price / unit.nights)} €/nuit`
                      : `${price} € · ${formatKmf(priceKMF)} KMF`);

                return (
                  <Box
                    key={index}
                    title={title}
                    onMouseDown={() => beginSelection("tarif", index, plan.id)}
                    onMouseEnter={() => extendSelection("tarif", index, plan.id)}
                    onMouseUp={endSelection}
                    sx={{
                      ...dayCell,
                      borderTop: `1px solid ${COLORS.borderSoft}`,
                      cursor: "pointer",
                      backgroundColor: isSelected
                        ? COLORS.selected
                        : price === null
                          ? COLORS.missing
                          : isDayWeekend(dateStr) ? COLORS.weekend : "#FFFFFF",
                      color: isSelected ? "#FFFFFF" : price === null ? COLORS.missingTxt : COLORS.text,
                      ...selectionRadius(isFirst, isLast),
                      "&:hover": { backgroundColor: isSelected ? COLORS.selected : "#EEF2FF" },
                    }}
                  >
                    {price !== null ? (
                      <>
                        <Box sx={{ fontSize: 13, fontWeight: 600, lineHeight: 1.2, whiteSpace: "nowrap" }}>
                          {price} €
                        </Box>
                        <Box sx={{
                          fontSize: 9.5,
                          lineHeight: 1.2,
                          whiteSpace: "nowrap",
                          color: isSelected ? "rgba(255,255,255,.75)" : COLORS.muted,
                        }}>
                          {formatKmf(priceKMF)} KMF
                        </Box>
                      </>
                    ) : "—"}
                  </Box>
                );
              })}
            </React.Fragment>
          );
        })
      )}

      {/* ── Stock ── */}
      <Box
        onClick={() => openBulk({ type: "inventory", roomId: room.roomId })}
        sx={{
          ...labelCell,
          borderTop: `1px solid ${COLORS.borderSoft}`,
          cursor: "pointer",
          "&:hover": { backgroundColor: "#F3F6F9" },
        }}
      >
        <Box sx={{ flex: 1 }}>Stock</Box>
        <Box sx={{ fontSize: 11, color: "#CBD5E1" }}>✎</Box>
      </Box>

      {visibleDays.map((dateStr, index) => {
        const dayData = room.days[startIndex + index];
        const available = dayData?.status === "closed" ? 0 : (dayData?.inventory ?? 0);

        const isSelected =
          editType === "inventory" &&
          editModeRoom === room.roomId &&
          selectedDays.inventory?.includes(index);

        const isFirst = isSelected && !selectedDays.inventory?.includes(index - 1);
        const isLast  = isSelected && !selectedDays.inventory?.includes(index + 1);

        return (
          <Box
            key={index}
            onMouseDown={() => beginSelection("inventory", index)}
            onMouseEnter={() => extendSelection("inventory", index)}
            onMouseUp={endSelection}
            sx={{
              ...dayCell,
              borderTop: `1px solid ${COLORS.borderSoft}`,
              cursor: "pointer",
              fontWeight: 600,
              backgroundColor: isSelected
                ? COLORS.selected
                : available === 0
                  ? COLORS.closed
                  : isDayWeekend(dateStr) ? COLORS.weekend : "#FFFFFF",
              color: isSelected ? "#FFFFFF" : available === 0 ? "#B91C1C" : COLORS.text,
              ...selectionRadius(isFirst, isLast),
              "&:hover": { backgroundColor: isSelected ? COLORS.selected : "#EEF2FF" },
            }}
          >
            {available}
          </Box>
        );
      })}

      {/* ── Statut ── */}
      <Box sx={{
        ...labelCell,
        borderTop: `1px solid ${COLORS.borderSoft}`,
        borderBottom: isLastRoom ? "none" : `1px solid ${COLORS.border}`,
      }}>
        Statut
      </Box>

      {visibleDays.map((dateStr, index) => {
        const dayData = room.days[startIndex + index];
        const isClosed = (dayData?.status ?? "closed") === "closed";

        const isSelected =
          editType === "status" &&
          editModeRoom === room.roomId &&
          selectedDays.status?.includes(index);

        const isFirst = isSelected && !selectedDays.status?.includes(index - 1);
        const isLast  = isSelected && !selectedDays.status?.includes(index + 1);

        return (
          <Box
            key={index}
            onMouseDown={() => beginSelection("status", index)}
            onMouseEnter={() => extendSelection("status", index)}
            onMouseUp={endSelection}
            sx={{
              ...dayCell,
              borderTop: `1px solid ${COLORS.borderSoft}`,
              borderBottom: isLastRoom ? "none" : `1px solid ${COLORS.border}`,
              cursor: "pointer",
              fontWeight: 700,
              fontSize: 12,
              backgroundColor: isSelected ? COLORS.selected : isClosed ? "#F5F7FA" : "#ECFDF3",
              color: isSelected ? "#FFFFFF" : isClosed ? "#64748B" : "#15803D",
              ...selectionRadius(isFirst, isLast),
              "&:hover": { backgroundColor: isSelected ? COLORS.selected : "#EEF2FF" },
            }}
          >
            {isClosed ? "Fermé" : "Ouvert"}
          </Box>
        );
      })}
    </>
  );
};

export default InventoryGrid;